import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const EYE_SUGGESTIONS = [
  "Describe the room",
  "Read any writing you can see",
  "What's the brand?",
  "What's on the desk?",
  "Is anyone there?",
  "Look at me — what am I holding?",
  "Count the objects",
  "What colour is it?",
  "What's behind me?",
  "Tell me everything you can see",
];

/**
 * EyePreviewTile — live feed from the ESP32-S3 camera. Polls
 * /api/jarvis/camera/latest every 2 seconds and re-renders the most
 * recent frame. Owner brief: "will it show a live video on my app of
 * what is looking at, yeah?"
 *
 * Plus a list of voice-prompt suggestions so the owner knows what to
 * ask. Tap a suggestion → it fires through the brain as if spoken.
 */
export default function EyePreviewTile() {
  const [imgSrc, setImgSrc] = useState("");
  const [staleSec, setStaleSec] = useState(0);
  const [error, setError] = useState("");
  const lastUploadedAtRef = useRef("");

  const askEye = (text) => {
    // Re-use the existing jarvis chat endpoint so the eye-question regex
    // on the backend fires and the spoken reply comes back through the
    // normal voice pipeline.
    try {
      window.dispatchEvent(new CustomEvent("rawco-send-command", { detail: { text } }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      // 🚨 PERF — don't poll when the tab is hidden. The 2-second cadence
      // accumulated dozens of in-flight requests when the owner left the
      // mirror tab open in the background, locking up the main thread on
      // return. visibilityState gate is cheap and aligns with how every
      // modern site (YouTube, Slack) pauses background work.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const r = await api.get("/jarvis/camera/latest");
        if (cancelled) return;
        const d = r.data || {};
        if (!d.image_base64) { setError("No frame yet — is the ESP32 powered & on Wi-Fi?"); return; }
        setError("");
        const uploadedAt = d.uploaded_at || "";
        if (uploadedAt !== lastUploadedAtRef.current) {
          lastUploadedAtRef.current = uploadedAt;
          setImgSrc(`data:image/jpeg;base64,${d.image_base64}`);
        }
        try {
          const dt = new Date(uploadedAt);
          setStaleSec(Math.max(0, Math.round((Date.now() - dt.getTime()) / 1000)));
        } catch { /* ignore */ }
      } catch (e) {
        if (!cancelled) setError("Eye not reachable");
      }
    };
    poll();
    // Bumped from 2000ms to 3000ms — at 2s the eye preview alone fired
    // 1800 requests/hr while idle, hammering the device's mobile data
    // and the React render budget. 3s is still effectively-live.
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <div data-testid="eye-preview-tile" style={{ position: "relative" }}>
      {imgSrc ? (
        <img
          src={imgSrc}
          alt="ESP32-S3 live feed"
          style={{
            width: "100%", display: "block", borderRadius: 6,
            border: "1px solid rgba(255,184,0,0.35)",
            boxShadow: "0 0 24px rgba(255,184,0,0.25)",
            transform: "rotate(180deg)",
          }}
        />
      ) : (
        <div style={{
          padding: 24, textAlign: "center", fontSize: 11,
          color: "var(--rc-gold)", opacity: 0.8,
          border: "1px dashed rgba(255,184,0,0.35)", borderRadius: 6,
        }}>
          {error || "Waiting for first frame…"}
        </div>
      )}
      <div style={{
        position: "absolute", top: 8, right: 8,
        background: "rgba(0,0,0,0.7)", color: "#3ed1ff",
        padding: "3px 7px", fontSize: 10, fontFamily: "monospace",
        letterSpacing: 1.5, borderRadius: 3,
      }}>
        ● LIVE · {staleSec}s ago
      </div>

      {/* 💡 Suggestion chips — owner asked: "make sure a little pop-up list
          comes on the camera screen of suggestions of what to say to the
          all-seeing eye." Click any chip to fire it through the brain. */}
      <div style={{ marginTop: 10, fontSize: 9, color: "rgba(255,184,0,0.7)",
                    letterSpacing: 2, textTransform: "uppercase" }}>
        SAY TO THE EYE
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
        {EYE_SUGGESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => askEye(s)}
            data-testid={`eye-suggestion-${s.replace(/\s+/g, "-").toLowerCase().slice(0, 24)}`}
            style={{
              padding: "5px 10px",
              fontSize: 11,
              fontFamily: "'Share Tech Mono', monospace",
              background: "rgba(255,184,0,0.08)",
              color: "var(--rc-gold)",
              border: "1px solid rgba(255,184,0,0.35)",
              borderRadius: 3,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,184,0,0.18)";
              e.currentTarget.style.boxShadow = "0 0 8px rgba(255,184,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,184,0,0.08)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
